// =============================================================================
// Entrada de dinheiro — do que a pessoa digita até os centavos que vão ao banco
//
// O caminho de volta do lib/formato.ts, e com o mesmo cuidado: em nenhum ponto
// o valor passa por float. `parseFloat("1234,56") * 100` devolve 123455.99999
// em alguns valores, e aí o orçado entra no banco um centavo menor do que o
// Financeiro digitou — erro que ninguém percebe até a conciliação não fechar.
// =============================================================================

/**
 * "R$ 1.234,56", "1234,56", "1.234" → "123456" (centavos, como string).
 * Devolve null quando não dá para ler um número.
 */
export function paraCentavos(entrada: string): string | null {
  const limpo = entrada.replace(/[^\d,.-]/g, "").trim();
  if (limpo === "" || limpo === "-") return null;

  const negativo = limpo.startsWith("-");
  const semSinal = negativo ? limpo.slice(1) : limpo;

  // Qual separador é o decimal. O ÚLTIMO ponto ou vírgula é candidato, e a
  // quantidade de dígitos depois dele decide:
  //
  //   1 ou 2 dígitos ...... decimal ("1234,56", "1.5")
  //   exatamente 3 ........ milhar ("1.234", "1,234") — é como o Financeiro
  //                         digita, e ler como decimal erraria por mil
  //   4 ou mais ........... decimal com casas a mais, arredondado ao centavo.
  //                         Grupo de milhar tem 3 dígitos, nunca 4: "411,3333333"
  //                         é o 411,33 de uma célula calculada do Excel (anual
  //                         ÷ 12). Até 18/09 virava 411 BILHÕES de centavos —
  //                         R$ 4,1 bi —, achado da revisão da faixa A.
  //
  // E com ponto E vírgula na mesma entrada, o último é o decimal sempre:
  // "1.234,567" é 1.234,57 — o ponto já é o milhar. Antes virava R$ 1.234.567.
  const ultimaVirgula = semSinal.lastIndexOf(",");
  const ultimoPonto = semSinal.lastIndexOf(".");
  const corte = Math.max(ultimaVirgula, ultimoPonto);
  const digitosDepois = corte >= 0 ? semSinal.length - corte - 1 : 0;
  const temOsDois = ultimaVirgula >= 0 && ultimoPonto >= 0;
  const ehDecimal = corte >= 0 && digitosDepois > 0 && (digitosDepois !== 3 || temOsDois);

  let inteiro: string;
  let decimais: string;

  if (ehDecimal) {
    inteiro = semSinal.slice(0, corte).replace(/[.,]/g, "");
    decimais = semSinal.slice(corte + 1);
  } else {
    inteiro = semSinal.replace(/[.,]/g, "");
    decimais = "";
  }

  if (!/^\d*$/.test(inteiro) || !/^\d*$/.test(decimais)) return null;
  if (inteiro === "" && decimais === "") return null;

  // Arredonda meio centavo para cima, sobre o valor absoluto — o sinal volta no
  // fim, então -411,335 vira -411,34, simétrico ao positivo.
  let valor = BigInt(`${inteiro || "0"}${decimais.padEnd(2, "0").slice(0, 2)}`);
  if (decimais.length > 2 && Number(decimais[2]) >= 5) valor += 1n;
  return (negativo ? -valor : valor).toString();
}

/** "123456" → "1.234,56", para reexibir no campo depois do blur. */
export function deCentavos(centavos: string | null): string {
  if (!centavos) return "";
  const n = BigInt(centavos);
  const negativo = n < 0n;
  const abs = negativo ? -n : n;
  const inteiro = new Intl.NumberFormat("pt-BR").format(abs / 100n);
  const dec = (abs % 100n).toString().padStart(2, "0");
  return `${negativo ? "-" : ""}${inteiro},${dec}`;
}
